<template lang="pug">
div
  b-card
    b-row.mb-1
      b-col(md="6")
        h4 {{ $t('arsopbl.channel_title') }}
        p.text-muted {{ $t('arsopbl.channel_subtitle') }}
      b-col(md="3", class="text-right")
        b-button(
          v-if="canExport",
          variant="outline-success",
          size="sm",
          :disabled="!filteredRequests.length",
          @click="handleExportExcel"
        )
          feather-icon(icon="DownloadIcon", class="mr-50")
          | {{ $t('arsopbl.export_excel') }}
      b-col(md="3")
        b-form-input(
          v-model="searchQuery",
          :placeholder="$t('message.SearchPlaceholder')",
          debounce="300"
        )

    //- Filters
    filters(
      :arsopblRightFilterShow="true",
      :arsopblStageFilterShow="true",
      :arsopblDeadlineFilterShow="true",
      :workerFilterShow="true",
      :workerPlaceholderVariable="'arsopbl.responsible_filter'",
      @changeFilter="handleFilterChange"
    )

    //- Table
    b-table(
      :items="paginatedRequests",
      :fields="fields",
      hover,
      responsive,
      show-empty="",
      :empty-text="$t('arsopbl.no_requests')",
      :busy="isLoading"
    )
      template(#table-busy)
        div.text-center.my-3
          loading(:active="true", :is-full-page="false", color="#498ceb")

      template(#cell(actions)="data")
        view-button(@clicked="goToDetail(data.item._id)")

      template(#cell(caseNumber)="data")
        b-link(:to="{ name: 'privacy-arsopbl-detail', params: { id: data.item._id } }")
          | {{ data.item.caseNumber }}

      template(#cell(rightCode)="data")
        b-badge(variant="light-primary") {{ $t(`arsopbl.right_${data.item.rightCode.toLowerCase()}`) }}

      template(#cell(titularName)="data")
        | {{ data.item.titularName }}

      template(#cell(createdAt)="data")
        | {{ formatDate(data.item.createdAt) }}

      template(#cell(stage)="data")
        b-badge(:variant="getStageVariant(data.item.stage)")
          | {{ $t(`arsopbl.stage_${data.item.stage}`) }}

      template(#cell(status)="data")
        b-badge(:variant="getStatusVariant(data.item.status)")
          | {{ $t(`arsopbl.status_${data.item.status}`) }}

      template(#cell(businessDays)="data")
        | {{ data.item.deadlineStatus.days }}

      template(#cell(deadline)="data")
        b-badge(:variant="data.item.deadlineStatus.variant")
          | {{ $t(`arsopbl.deadline_${data.item.deadlineStatus.label}`) }}

      template(#cell(responsableName)="data")
        | {{ data.item.responsableName || '-' }}

    //- Pagination
    b-row
      b-col(cols="12", class="d-flex justify-content-between flex-wrap")
        div.mb-1
          b-form-group.mb-0
            b-form-select#perPageSelect(
              v-model="perPage",
              size="sm",
              :options="[5, 10, 15, 20, 25, 30, 40, 50, 100]",
              class="per-page-selector"
            )
        div.mb-1
          span.text-muted {{ $tc('message.paginationText', 0, { from: `${((currentPage * perPage) - perPage) + (paginatedRequests.length ? 1 : 0)}`, to: `${paginatedRequests.length + ((currentPage * perPage) - perPage)}`, total: `${totalRequests}` }) }}
        b-pagination(
          v-model="currentPage",
          :total-rows="totalRequests",
          :per-page="perPage",
          first-number="",
          last-number="",
          prev-class="prev-item",
          next-class="next-item"
        )
</template>

<script>
import { ref, computed, onMounted, watch } from '@vue/composition-api'
import { BBadge, BLink, BTable, BPagination, BFormSelect, BFormGroup } from 'bootstrap-vue'
import store from '@/store'
import i18n from '@/libs/i18n'
import { useRouter } from '@core/utils/utils'
import { hasPermission } from '@/composables/usePermission'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import useCommonArsopbl from '../useCommonArsopbl'
import useExportArsopbl from './useExportArsopbl'
import Filters from '@/views/organization/Filters.vue'
import ViewButton from '@/views/components/Shared/Buttons/ViewButton.vue'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

export default {
  components: { BBadge, BLink, BTable, BPagination, BFormSelect, BFormGroup, Filters, ViewButton, Loading },
  setup() {
    const { router } = useRouter()
    const { getItemsAggregate } = useLambda()
    const { collection, getDeadlineStatus } = useCommonArsopbl()
    const { exportToExcel } = useExportArsopbl()
    const userData = store.state?.userStore?.userData

    const requests = ref([])
    const isLoading = ref(true)
    const searchQuery = ref('')
    const canExport = computed(() => hasPermission('privacy-arsopbl-list', 'export'))

    // Pagination
    const perPage = ref(10)
    const currentPage = ref(1)
    const totalRequests = ref(0)

    // Filter values from Filters.vue
    const activeFilters = ref({})

    const fields = [
      { key: 'actions', label: i18n.t('message.tableHeader.actions'), sortable: false },
      { key: 'caseNumber', label: i18n.t('arsopbl.case_number'), sortable: true },
      { key: 'rightCode', label: i18n.t('arsopbl.right'), sortable: true },
      { key: 'titularName', label: i18n.t('arsopbl.titular') },
      { key: 'createdAt', label: i18n.t('arsopbl.reception_date'), sortable: true },
      { key: 'stage', label: i18n.t('arsopbl.stage'), sortable: true },
      { key: 'status', label: i18n.t('arsopbl.status'), sortable: true },
      { key: 'businessDays', label: i18n.t('arsopbl.days_elapsed') },
      { key: 'deadline', label: i18n.t('arsopbl.deadline_status') },
      { key: 'responsableName', label: i18n.t('arsopbl.responsible') },
    ]

    const filteredRequests = computed(() => {
      let result = requests.value
      if (activeFilters.value.arsopblRightFilter) {
        result = result.filter(r => r.rightCode === activeFilters.value.arsopblRightFilter)
      }
      if (activeFilters.value.arsopblStageFilter) {
        result = result.filter(r => r.stage === activeFilters.value.arsopblStageFilter)
      }
      if (activeFilters.value.arsopblDeadlineFilter) {
        result = result.filter(r => r.deadlineStatus.label === activeFilters.value.arsopblDeadlineFilter)
      }
      if (activeFilters.value.workerFilter && activeFilters.value.workerFilter.length) {
        result = result.filter(r => activeFilters.value.workerFilter.includes(r.executionResponsible))
      }
      if (searchQuery.value) {
        const search = searchQuery.value.toLowerCase()
        result = result.filter(r =>
          (r.caseNumber && r.caseNumber.toLowerCase().includes(search))
          || (r.titularName && r.titularName.toLowerCase().includes(search)),
        )
      }
      return result
    })

    const paginatedRequests = computed(() => {
      const start = (currentPage.value - 1) * perPage.value
      const end = start + perPage.value
      return filteredRequests.value.slice(start, end)
    })

    // Watch filteredRequests to update totalRequests and reset to page 1
    watch(filteredRequests, (newVal) => {
      totalRequests.value = newVal.length
      currentPage.value = 1
    })

    // Watch perPage to reset to page 1
    watch(perPage, () => {
      currentPage.value = 1
    })

    const handleFilterChange = (filters) => {
      activeFilters.value = filters
    }

    const formatDate = (dateValue) => {
      if (!dateValue) return '-'
      const raw = dateValue?.$date || dateValue
      const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
      return new Date(raw).toLocaleDateString(`${default_language || 'es'}-CL`, { timeZone: 'UTC' })
    }

    const goToDetail = (id) => {
      router.push({ name: 'privacy-arsopbl-detail', params: { id } })
    }

    const handleExportExcel = () => {
      if (!hasPermission('privacy-arsopbl-list', 'export')) return
      exportToExcel(filteredRequests.value)
    }

    const getStatusVariant = (status) => {
      const variants = {
        open: 'light-info',
        resolved: 'light-success',
        rejected: 'light-danger',
      }
      return variants[status] || 'light-secondary'
    }

    const getStageVariant = (stage) => {
      const variants = {
        reception: 'light-info',
        identity_validation: 'light-warning',
        analysis: 'light-primary',
        execution: 'light-info',
        response: 'light-success',
        closure: 'light-secondary',
      }
      return variants[stage] || 'light-info'
    }

    const fetchRequests = async () => {
      isLoading.value = true
      try {
        const pipeline = [
          { $match: { client_id: oid(userData.client.$oid) } },
          {
            $lookup: {
              from: 'worker',
              localField: 'executionResponsible',
              foreignField: '_id',
              as: 'responsableData',
              pipeline: [{ $project: { name: 1 } }],
            },
          },
          { $sort: { createdAt: -1 } },
          {
            $addFields: {
              _id: { $toString: '$_id' },
              responsableName: { $arrayElemAt: ['$responsableData.name', 0] },
            },
          },
        ]

        const items = await getItemsAggregate({ collection, pipeline })

        requests.value = (items || []).map(item => ({
          ...item,
          titularName: (item.titular && item.titular.fullName) || '-',
          deadlineStatus: getDeadlineStatus(item.createdAt?.$date || item.createdAt),
          executionResponsible: item.executionResponsible ? (item.executionResponsible.$oid || item.executionResponsible.toString()) : null,
        }))

        // Initialize totalRequests
        totalRequests.value = requests.value.length
      } catch (error) {
        console.log(error)
      } finally {
        isLoading.value = false
      }
    }

    onMounted(fetchRequests)

    return {
      paginatedRequests,
      filteredRequests,
      isLoading,
      searchQuery,
      canExport,
      fields,
      perPage,
      currentPage,
      totalRequests,
      handleFilterChange,
      formatDate,
      getStatusVariant,
      getStageVariant,
      goToDetail,
      handleExportExcel,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>
